import { isValidEmail } from "../../../utils/formatUtils";

const validateEmail = (value) => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return "Please enter email";
  }
  if (!isValidEmail(trimmed)) {
    return "Please enter a valid email address";
  }
  return null;
};

const validateAlternatePhone = (value, mobileNumber) => {
  const input = (value || "").trim();
  if (!mobileNumber && !input) {
    return "Please enter alternative";
  }
  if (input) {
    const hasPlus = input.startsWith("+");
    const digitsOnly = input.replace(/\D/g, "");

    if (hasPlus) {
      // International validation: starts with '+', followed by 7 to 15 digits
      const intlRegex = /^\+(\d[\s-.()]*){7,15}$/;
      if (!intlRegex.test(input)) {
        return "Please enter a valid phone number starting with + and country code";
      }
    } else {
      // Local validation: exactly 10 digits
      const localRegex = /^(\d[\s-.()]*){10}$/;
      if (!localRegex.test(input)) {
        return "Please enter a valid 10-digit phone number";
      }
    }

    const hasRepeatingDigits = /(\d)\1{5,}/.test(digitsOnly);
    const isSequential =
      "01234567890123456789".includes(digitsOnly) ||
      "98765432109876543210".includes(digitsOnly);
    if (hasRepeatingDigits || (isSequential && digitsOnly.length >= 5)) {
      return "Please enter a valid phone number";
    }
  }
  return null;
};

const validateAlternateEmail = (value, email) => {
  const trimmed = value?.trim();
  if (!email && !trimmed) {
    return "Please enter alternative email";
  }
  if (trimmed && !isValidEmail(trimmed)) {
    return "Please enter a valid email address";
  }
  return null;
};

const validateContactMethod = (value, mobileNumber, alternatePhone) => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return "Please select a contact method";
  }
  if (trimmed === "Mobile Phone" && !mobileNumber) {
    return "Preferred communication method must not be mobile phone if a mobile phone number is not provided.";
  }
  if (trimmed === "Alternate Phone" && !alternatePhone) {
    return "Preferred communication method must not be alternate phone if an alternate phone number is not provided.";
  }
  return null;
};

/**
 * Validates a single field in the General and Contact Details form.
 * @param {string} name - Field name
 * @param {string} value - Field value
 * @param {object} formData - Form data object
 * @returns {string|null} - Error message or null if valid
 */
export const validateGeneralContactField = (name, value, formData = {}) => {
  if (name === "email") {
    return validateEmail(value);
  }
  if (name === "alternatePhone") {
    return validateAlternatePhone(value, formData.mobileNumber);
  }
  if (name === "alternateEmail") {
    return validateAlternateEmail(value, formData.email);
  }
  if (name === "contactMethod" || name === "refillContactMethod") {
    return validateContactMethod(
      value,
      formData.mobileNumber,
      formData.alternatePhone,
    );
  }
  return null;
};

/**
 * Validates the entire General and Contact Details form data.
 * @param {object} formData - Form data object
 * @returns {object} - Object containing errors for each field
 */
export const validateGeneralContactForm = (formData) => {
  const errors = {};

  const emailValError = validateGeneralContactField(
    "email",
    formData.email || "",
    formData,
  );
  if (emailValError) errors.email = emailValError;

  const phoneError = validateGeneralContactField(
    "alternatePhone",
    formData.alternatePhone || "",
    formData,
  );
  if (phoneError) errors.alternatePhone = phoneError;

  const emailError = validateGeneralContactField(
    "alternateEmail",
    formData.alternateEmail || "",
    formData,
  );
  if (emailError) errors.alternateEmail = emailError;

  const contactMethodError = validateGeneralContactField(
    "contactMethod",
    formData.contactMethod || "",
    formData,
  );
  if (contactMethodError) errors.contactMethod = contactMethodError;

  const refillContactMethodError = validateGeneralContactField(
    "refillContactMethod",
    formData.refillContactMethod || "",
    formData,
  );
  if (refillContactMethodError)
    errors.refillContactMethod = refillContactMethodError;

  return errors;
};
